import { Spinner, Text, Flex, Box, Button } from "@chakra-ui/react";
import { CardContainer } from "./style";
import { useCertificateContext } from "@root/context/certificate-context";
import { BorderIcon } from "@root/assets/icons/border";
import { UnloackIcon } from "@root/assets/icons/UnloackIcon";
import { useTranslation } from "react-i18next";

const CertificateCard = ({
  id,
  courseTitle,
  completionDate,
  organizatinoName,
  journalistName,
}: {
  id: number;
  courseTitle: string;
  completionDate: string;
  organizatinoName: string;
  journalistName: string;
}) => {
  const { fetchCertificate, isCertificateLoading } = useCertificateContext();
  const { t } = useTranslation("common");

  const handleDownload = () => {
    fetchCertificate({ courseId: id, gmtOffset: 0 });
  };

  return (
    <CardContainer
      key={id}
      position="relative"
      flexDirection="column"
      border="1px solid #CDD6DC"
      borderRadius="8px"
      overflow="hidden"
      backgroundColor="#fff"
      p="10px 10px 0px 10px"
      width="100%"
      minHeight={{ base: "220px", sm: "240px", md: "270px" }}
    >
      {/* Certificate Design */}
      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {/* Border Design */}
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          zIndex={6}
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
        >
          <BorderIcon
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top center",
            }}
          />
        </Box>

        {/* Serial Number */}
        <Text
          fontSize={{ base: "10px", md: "12px" }}
          fontWeight="500"
          color="#666"
          fontFamily="certificate"
          position="absolute"
          top="16px"
          left="16px"
          zIndex={5}
        >
          {t("kpecDashboard.certificateNumber")}
        </Text>

        {/* Certificate Content */}
        <Box
          width="calc(100% - 24px)"
          height="calc(100% - 24px)"
          backgroundColor="white"
          borderRadius="8px"
          p={{ base: "8px", md: "10px" }}
          position="relative"
          zIndex={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
        >
          <Text
            fontSize={{ base: "15px", sm: "17px", md: "20px" }}
            fontWeight="500"
            mb={{ base: "12px", md: "20px" }}
            mt={{ base: "24px", md: "30px" }}
            textAlign="center"
            lineHeight="1.2"
            fontFamily="certificateBold"
          >
            {t("kpecDashboard.certificateOfCourseCompletion")}
          </Text>

          <Box width="100%" textAlign="left" mb={{ base: "12px", md: "20px" }}>
            {[
              { label: t("kpecDashboard.subscriberName"), value: organizatinoName },
              { label: t("kpecDashboard.coursePeriod"), value: completionDate },
              { label: t("kpecDashboard.courseName"), value: courseTitle },
              { label: t("kpecDashboard.courseParticipant"), value: journalistName },
            ].map(({ label, value }) => (
              <Flex alignItems="flex-start" mb="6px" key={label}>
                <Box
                  width="4px"
                  height="4px"
                  backgroundColor="black"
                  mr="8px"
                  mt="6px"
                  flexShrink={0}
                />
                <Text
                  fontSize={{ base: "10px", md: "12px" }}
                  fontFamily="certificate"
                  lineHeight="1.4"
                >
                  {label}: {value}
                </Text>
              </Flex>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Download Button */}
      <Button
        onClick={handleDownload}
        isLoading={isCertificateLoading(id)}
        rightIcon={<UnloackIcon width="16" height="16" boxSize={6} />}
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex={10}
        width={{ base: "140px", sm: "160px", md: "200px" }}
        height={{ base: "32px", sm: "36px", md: "40px" }}
        backgroundColor="#1819B4"
        color="#FFFFFF"
        borderRadius="8px"
        fontSize={{ base: "11px", sm: "12px", md: "14px" }}
        fontWeight="600"
        border="1px solid #FFFFFF"
        _hover={{ backgroundColor: "#F7F8FF" }}
      >
        {isCertificateLoading(id) ? (
          <Flex align="center" gap={2}>
            <Spinner size="sm" color="#1819B4" />
            <Text color="#1819B4">{t("kpecDashboard.downloading")}</Text>
          </Flex>
        ) : (
          <Text>{t("kpecDashboard.getYourCertificate")}</Text>
        )}
      </Button>
    </CardContainer>
  );
};

export default CertificateCard;
